/**
 * 플랫폼 문의(support_messages) 사진 첨부 규칙.
 *
 * locarora-admin `lib/chat/support-attachments.ts` 와 **같은 내용**을 유지한다.
 *
 * 사진은 chat-attachments 버킷의 `{conversationId}/...` 에 저장한다.
 * attachments 는 보내는 쪽이 넘기는 값이고 RLS 로 직접 넣을 수도 있으므로 믿지 않는다:
 * 저장할 때도 그릴 때도 "이 대화 폴더의 사진"만 인정하고, 주소는 경로로 다시 만든다.
 * 호스트 없는 경로라 서명이 실패해도 외부 주소를 불러오는 일이 없다.
 */
import type { LangCode, MessageAttachment } from "@/types/message";
import { isPhotoOnlyPlaceholder } from "./attachment-placeholder";
import {
  CHAT_ATTACHMENTS_BUCKET,
  chatAttachmentPath,
} from "./chat-attachment-url";

export const SUPPORT_IMAGE_MAX_FILES = 5;
export const SUPPORT_IMAGE_MAX_MB = 10;
export const SUPPORT_IMAGE_MAX_BYTES = SUPPORT_IMAGE_MAX_MB * 1024 * 1024;
export const SUPPORT_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

/** 글 없이 사진만 보낼 때 content 에 넣는 문구 — 목록 미리보기·푸시용. attachment-placeholder 와 같은 문구. */
const PHOTO_ONLY_TEXT: Record<LangCode, string> = {
  ko: "파일을 보냈습니다",
  en: "File sent",
  ja: "ファイルを送信しました",
  "zh-TW": "檔案已發送",
  "zh-CN": "文件已发送",
  th: "ส่งไฟล์แล้ว",
  vi: "Đã gửi tệp",
  id: "File terkirim",
};

export function supportPhotoOnlyText(lang: LangCode): string {
  return PHOTO_ONLY_TEXT[lang] ?? PHOTO_ONLY_TEXT.en;
}

function canonicalUrl(path: string): string {
  return `/storage/v1/object/public/${CHAT_ATTACHMENTS_BUCKET}/${path}`;
}

/** 이 대화 폴더의 사진 경로면 그 경로, 아니면 null. */
function ownImagePath(attachment: unknown, conversationId: string): string | null {
  if (!attachment || typeof attachment !== "object") return null;
  const a = attachment as { type?: unknown; url?: unknown };
  if (a.type !== "image") return null;
  const path = chatAttachmentPath(a.url);
  return path?.startsWith(`${conversationId}/`) ? path : null;
}

/**
 * 저장할 첨부로 바꾼다. 없으면 빈 배열, 하나라도 규칙에 어긋나면 null(발송 거부).
 */
export function sanitizeSupportAttachments(
  input: unknown,
  conversationId: string,
): MessageAttachment[] | null {
  if (input === undefined || input === null) return [];
  if (!Array.isArray(input) || input.length > SUPPORT_IMAGE_MAX_FILES) return null;
  const out: MessageAttachment[] = [];
  for (const item of input) {
    const path = ownImagePath(item, conversationId);
    if (!path) return null;
    const { name, size } = item as { name?: unknown; size?: unknown };
    out.push({
      url: canonicalUrl(path),
      type: "image",
      name: typeof name === "string" ? name.slice(0, 200) : "image",
      size: typeof size === "number" && size >= 0 ? size : 0,
    });
  }
  return out;
}

/** 화면에 그릴 사진. 다른 폴더·외부 주소는 건너뛴다. */
export function supportMessageImages(message: {
  conversation_id: string;
  attachments?: unknown;
}): { url: string; name: string }[] {
  if (!Array.isArray(message.attachments)) return [];
  const out: { url: string; name: string }[] = [];
  for (const item of message.attachments) {
    const path = ownImagePath(item, message.conversation_id);
    if (!path) continue;
    const { name } = item as { name?: unknown };
    out.push({ url: canonicalUrl(path), name: typeof name === "string" ? name : "image" });
  }
  return out;
}

/** 글 없이 사진만 있는 메시지 — 말풍선 없이 사진만 그린다. */
export function isSupportPhotoOnly(message: {
  conversation_id: string;
  content?: string | null;
  attachments?: unknown;
}): boolean {
  const images = supportMessageImages(message);
  if (images.length === 0) return false;
  return isPhotoOnlyPlaceholder({
    content: message.content,
    attachments: images.map(() => ({ type: "image" })),
  });
}
